// src/pages/builder/AvatarItemsManager.js
//
// 🧥 UPLOAD AVATAR ITEMS — Layout Builder → Rewards & avatar settings.
//
// Add jackets & shirts, sneakers, and mustaches & beards to the Avatar
// Maker from a picture, with no code change:
//
//   1. Pick a picture (PNG, JPG or WebP). A see-through PNG is used as it
//      is; otherwise the plain background around the item (white or any
//      flat colour) is cut out automatically, the picture is trimmed to the
//      item, and sized for the avatar.
//   2. Pick the category, then EVERYDAY (Level 1–4) or a SEASON (it shows
//      in that holiday inside the Avatar Maker, and sells only in its dates).
//   3. Name and price (0 = free).
//   4. Check the preview on the avatar and nudge the fit with the sliders.
//   5. Save — it appears in every student's Avatar Maker straight away.
//
// Saved items can be renamed, re-priced, moved, hidden or deleted below.
// Data: avatarItems/{id} + topic-images/avatar-items/{id}.png
// (services/avatarItemsService.js). The server prices them from Firestore
// (functions/shop.js), so the browser never decides a price.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AvatarFace, DEFAULT_LOOK } from "../../components/avatar/avatarParts";
import { previewAvatarItem } from "../../components/avatar/avatarCatalog";
import { SEASONS, seasonOf } from "../../components/avatar/seasonItems";
import {
  deleteAvatarItem,
  newAvatarItemId,
  saveAvatarItem,
  updateAvatarItem,
  uploadAvatarItemPicture,
  useAvatarItemsVersion,
  useUploadedAvatarItems,
} from "../../services/avatarItemsService";
import "./avatarItems.css";

const KINDS = [
  { id: "top", label: "🧥 Jackets & shirts", key: "top", tip: "Front view, collar at the top, sleeves hanging down." },
  { id: "shoes", label: "👟 Sneakers", key: "shoes", tip: "One shoe from the side, toe pointing LEFT (tick “flip” if it points right)." },
  { id: "mustache", label: "🧔 Mustaches & beards", key: "mustache", tip: "Just the hair, on a plain background — no face." },
];
// where a beard sits on the face: [top, width] in the 100×100 face box
const BEARD_STYLES = [
  { id: "must", label: "Mustache only", fit: [53.2, 19], layer: "over" },
  { id: "mg", label: "Mustache + goatee", fit: [53, 22], layer: "over" },
  { id: "chin", label: "Chin / goatee only", fit: [60, 10], layer: "under" },
  { id: "ring", label: "Beard around the mouth", fit: [53, 20], layer: "under" },
  { id: "jaw", label: "Beard along the jaw (no mustache)", fit: [47.5, 45], layer: "under" },
  { id: "full", label: "Full beard with mustache", fit: [51.5, 45], layer: "under" },
  { id: "stubble", label: "Stubble", fit: [47.5, 44], layer: "under" },
];
const PREVIEW_ID = "av-up-preview";
const LEVELS = [1, 2, 3, 4];

/* ------------------------------------------------------------------ */
/* Picture → cut out, trimmed, sized                                   */
/* ------------------------------------------------------------------ */

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That picture couldn't be opened."));
    img.src = src;
  });

/**
 * Cuts the item out of its picture.
 *   strength 0–100: how different from the background a pixel must be to
 *                   count as the item (higher = cuts more)
 *   holes:          also clear background-coloured spots INSIDE the item
 *                   (a beard's mouth gap)
 *   keepPieces:     keep every speck (stubble), not just the main item
 * Resolves { blob, url, ar, sh } — sh = how far down the shoulders are.
 */
export const prepareItemPicture = async (file, { strength = 40, holes = false, keepPieces = false, flip = false } = {}) => {
  const srcUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(srcUrl);
    const scale = Math.min(1, 900 / Math.max(img.naturalWidth, img.naturalHeight));
    const W = Math.max(1, Math.round(img.naturalWidth * scale));
    const H = Math.max(1, Math.round(img.naturalHeight * scale));
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (flip) {
      ctx.translate(W, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(img, 0, 0, W, H);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const d = ctx.getImageData(0, 0, W, H);
    const px = d.data;

    // already see-through? (transparent corners) → use its own alpha
    const cornerAlpha = [0, W - 1, (H - 1) * W, H * W - 1].map((p) => px[p * 4 + 3]);
    const seeThrough = cornerAlpha.every((a) => a < 20);

    if (!seeThrough) {
      // the background colour = the middle value of the edge pixels
      const edge = [];
      for (let x = 0; x < W; x += 2) edge.push(x, (H - 1) * W + x);
      for (let y = 0; y < H; y += 2) edge.push(y * W, y * W + W - 1);
      const med = [0, 1, 2].map((ch) => {
        const v = edge.map((p) => px[p * 4 + ch]).sort((a, b) => a - b);
        return v[v.length >> 1];
      });
      const tol = 18 + (strength / 100) * 90; // colour distance
      const dist = (p) => {
        const i = p * 4;
        return Math.hypot(px[i] - med[0], px[i + 1] - med[1], px[i + 2] - med[2]);
      };
      const clear = (p, dd) => {
        // soft edge: close to the background → clear, near the limit → part
        const a = dd <= tol * 0.6 ? 0 : Math.round(((dd - tol * 0.6) / (tol * 0.4)) * 255);
        px[p * 4 + 3] = Math.min(px[p * 4 + 3], a);
      };
      if (holes) {
        for (let p = 0; p < W * H; p += 1) {
          const dd = dist(p);
          if (dd < tol) clear(p, dd);
        }
      } else {
        const seen = new Uint8Array(W * H);
        const stack = edge.slice();
        while (stack.length) {
          const p = stack.pop();
          if (seen[p]) continue;
          seen[p] = 1;
          const dd = dist(p);
          if (dd >= tol) continue;
          clear(p, dd);
          const x = p % W;
          const y = (p / W) | 0;
          if (x > 0) stack.push(p - 1);
          if (x < W - 1) stack.push(p + 1);
          if (y > 0) stack.push(p - W);
          if (y < H - 1) stack.push(p + W);
        }
      }
    }

    // keep the item: the biggest solid piece(s), unless every speck counts
    const solid = new Uint8Array(W * H);
    for (let p = 0; p < W * H; p += 1) solid[p] = px[p * 4 + 3] > 40 ? 1 : 0;
    const label = new Int32Array(W * H);
    const sizes = [0];
    let n = 0;
    for (let p0 = 0; p0 < W * H; p0 += 1) {
      if (!solid[p0] || label[p0]) continue;
      n += 1;
      let size = 0;
      const stack = [p0];
      label[p0] = n;
      while (stack.length) {
        const p = stack.pop();
        size += 1;
        const x = p % W;
        const y = (p / W) | 0;
        const nb = [x > 0 ? p - 1 : -1, x < W - 1 ? p + 1 : -1, y > 0 ? p - W : -1, y < H - 1 ? p + W : -1];
        nb.forEach((q) => {
          if (q >= 0 && solid[q] && !label[q]) {
            label[q] = n;
            stack.push(q);
          }
        });
      }
      sizes.push(size);
    }
    if (n === 0) throw new Error("Nothing was left after cutting out the background — lower the cut-out strength.");
    const biggest = Math.max(...sizes);
    const keep = sizes.map((sz) => (keepPieces ? sz >= 4 : sz >= biggest * 0.1));
    let x0 = W;
    let y0 = H;
    let x1 = 0;
    let y1 = 0;
    for (let p = 0; p < W * H; p += 1) {
      if (label[p] && !keep[label[p]]) px[p * 4 + 3] = 0;
      if (px[p * 4 + 3] > 16) {
        const x = p % W;
        const y = (p / W) | 0;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    ctx.putImageData(d, 0, 0);
    const bw = x1 - x0 + 1;
    const bh = y1 - y0 + 1;

    // shoulders: the first row (from the top) as wide as most of the item
    let sh = 0.16;
    for (let y = y0; y <= y1; y += 1) {
      let left = -1;
      let right = -1;
      for (let x = x0; x <= x1; x += 1) {
        if (px[(y * W + x) * 4 + 3] > 40) {
          if (left < 0) left = x;
          right = x;
        }
      }
      if (left >= 0 && right - left + 1 >= bw * 0.72) {
        sh = (y - y0) / bh;
        break;
      }
    }

    const out = document.createElement("canvas");
    const k = Math.min(1, 420 / Math.max(bw, bh));
    out.width = Math.max(1, Math.round(bw * k));
    out.height = Math.max(1, Math.round(bh * k));
    const octx = out.getContext("2d");
    octx.imageSmoothingQuality = "high";
    octx.drawImage(c, x0, y0, bw, bh, 0, 0, out.width, out.height);
    const blob = await new Promise((r) => out.toBlob(r, "image/png"));
    return { blob, url: URL.createObjectURL(blob), ar: Math.round((bw / bh) * 1000) / 1000, sh: Math.round(sh * 1000) / 1000, seeThrough };
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
};

/* ------------------------------------------------------------------ */
/* Add one item                                                        */
/* ------------------------------------------------------------------ */

const Slider = ({ label, value, min, max, step, onChange, hint }) => (
  <label className="avi-slider">
    <span>{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    {hint && <small>{hint}</small>}
  </label>
);

const AddItem = ({ onSaved }) => {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("top");
  const [where, setWhere] = useState("everyday"); // everyday | season
  const [level, setLevel] = useState(1);
  const [season, setSeason] = useState(SEASONS[0]?.id || "");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("0");
  const [strength, setStrength] = useState(40);
  const [flip, setFlip] = useState(false);
  const [pair, setPair] = useState(false);
  const [short, setShort] = useState(false);
  const [beardStyle, setBeardStyle] = useState("full");
  const [keepColor, setKeepColor] = useState(false);
  const [pic, setPic] = useState(null); // { blob, url, ar, sh, seeThrough }
  const [sh, setSh] = useState(0.16);
  const [hem, setHem] = useState(0.4);
  const [fit, setFit] = useState([51.5, 45]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);
  useAvatarItemsVersion();

  const style = BEARD_STYLES.find((b) => b.id === beardStyle) || BEARD_STYLES[0];

  // (re)make the cut-out whenever the picture or a cut-out setting changes
  useEffect(() => {
    if (!file) return undefined;
    let alive = true;
    setBusy("cut");
    prepareItemPicture(file, {
      strength,
      flip: kind === "shoes" && flip,
      holes: kind === "mustache" && !keepColor,
      keepPieces: kind === "mustache" && beardStyle === "stubble",
    })
      .then((p) => {
        if (!alive) return;
        setPic((old) => {
          if (old?.url) URL.revokeObjectURL(old.url);
          return p;
        });
        setSh(p.sh);
        setMsg(null);
      })
      .catch((e) => alive && setMsg({ tone: "bad", text: e?.message || String(e) }))
      .finally(() => alive && setBusy(""));
    return () => {
      alive = false;
    };
  }, [file, strength, flip, kind, keepColor, beardStyle]);

  // sensible starting fit for each kind / shoe shape / beard style
  useEffect(() => {
    if (pic && kind === "shoes") setHem(pic.ar < 1.3 ? 0.22 : 0.4);
  }, [pic, kind]);
  useEffect(() => {
    setFit(style.fit);
  }, [style]);

  const item = useMemo(() => {
    if (!pic) return null;
    const base = { id: PREVIEW_ID, kind, name: name || "New item", price: Number(price) || 0, img: pic.url, ar: pic.ar };
    if (kind === "top") return { ...base, sh, ...(short ? { arms: "short" } : {}) };
    if (kind === "shoes") return { ...base, hem, ...(pair ? { pair: true } : {}) };
    return { ...base, fit, layer: style.layer, ...(keepColor ? { keepColor: true } : {}) };
  }, [pic, kind, name, price, sh, short, hem, pair, fit, style, keepColor]);

  useEffect(() => {
    if (item) previewAvatarItem(item);
  }, [item]);

  const key = KINDS.find((k) => k.id === kind)?.key || "top";
  const look = { ...DEFAULT_LOOK, [key]: PREVIEW_ID };

  const reset = () => {
    if (pic?.url) URL.revokeObjectURL(pic.url);
    setPic(null);
    setFile(null);
    setName("");
    setPrice("0");
    setFlip(false);
    setPair(false);
    setShort(false);
    setKeepColor(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!pic || !item) return;
    if (!name.trim()) {
      setMsg({ tone: "bad", text: "Give it a name first." });
      return;
    }
    setBusy("save");
    setMsg(null);
    try {
      const id = newAvatarItemId(name);
      const up = await uploadAvatarItemPicture(id, pic.blob);
      await saveAvatarItem(id, {
        ...item,
        id,
        isNew: true,
        name: name.trim(),
        img: up.url,
        path: up.path,
        level: where === "everyday" ? level : 1,
        season: where === "season" ? season : "",
      });
      const placeName = where === "season" ? `${seasonOf(season)?.name || season} season` : `Level ${level}`;
      setMsg({ tone: "ok", text: `Saved! “${name.trim()}” is in the Avatar Maker (${placeName}).` });
      reset();
      onSaved?.();
    } catch (e) {
      setMsg({
        tone: "bad",
        text:
          e?.code === "permission-denied" || e?.code === "storage/unauthorized"
            ? "Permission denied — deploy the new firestore.rules (avatarItems block) and use an admin account."
            : `Couldn't save: ${e?.message || e}`,
      });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="avi-add">
      <div className="avi-form">
        <label className="avi-field">
          <span>1. Picture</span>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <small>A see-through PNG is used as it is. On a plain background (white or any flat colour) the background is cut out for you.</small>
        </label>

        <div className="avi-field">
          <span>2. Category</span>
          <div className="avi-seg">
            {KINDS.map((k) => (
              <button key={k.id} type="button" className={kind === k.id ? "is-on" : ""} onClick={() => setKind(k.id)}>
                {k.label}
              </button>
            ))}
          </div>
          <small>{KINDS.find((k) => k.id === kind)?.tip}</small>
        </div>

        <div className="avi-field">
          <span>3. Where it goes</span>
          <div className="avi-seg">
            <button type="button" className={where === "everyday" ? "is-on" : ""} onClick={() => setWhere("everyday")}>
              Everyday
            </button>
            <button type="button" className={where === "season" ? "is-on" : ""} onClick={() => setWhere("season")}>
              🎉 A season
            </button>
          </div>
          {where === "everyday" ? (
            <select value={level} onChange={(e) => setLevel(Number(e.target.value))} aria-label="Level">
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  Level {l} {"⭐".repeat(l)}
                </option>
              ))}
            </select>
          ) : (
            <select value={season} onChange={(e) => setSeason(e.target.value)} aria-label="Season">
              {SEASONS.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.emoji} {x.name}
                </option>
              ))}
            </select>
          )}
          {where === "season" && <small>Shows inside that holiday in the Avatar Maker; can be bought only in the holiday's dates.</small>}
        </div>

        <div className="avi-row">
          <label className="avi-field">
            <span>4. Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Red Hoodie" maxLength={60} />
          </label>
          <label className="avi-field avi-price">
            <span>Price (coins)</span>
            <input type="number" min="0" step="10" value={price} onChange={(e) => setPrice(e.target.value)} />
            <small>{Number(price) > 0 ? `${Number(price)} coins` : "Free for everyone"}</small>
          </label>
        </div>

        {pic && (
          <div className="avi-field">
            <span>5. Fit it to the avatar</span>
            {!pic.seeThrough && (
              <Slider label="Cut-out strength" value={strength} min={0} max={100} step={5} onChange={setStrength} hint="Raise it if bits of background are left; lower it if parts of the item disappear." />
            )}
            {kind === "top" && (
              <>
                <Slider label="Up / down" value={sh} min={0} max={0.5} step={0.01} onChange={setSh} hint="Move it until the collar sits under the chin." />
                <label className="sa-check">
                  <input type="checkbox" checked={short} onChange={(e) => setShort(e.target.checked)} /> Short sleeves (show bare arms)
                </label>
              </>
            )}
            {kind === "shoes" && (
              <>
                <Slider label="Pants go into the shoe" value={hem} min={0} max={0.8} step={0.02} onChange={setHem} hint="Slide until the hole where the foot goes is hidden." />
                <label className="sa-check">
                  <input type="checkbox" checked={flip} onChange={(e) => setFlip(e.target.checked)} /> The toe points right — flip it
                </label>
                <label className="sa-check">
                  <input type="checkbox" checked={pair} onChange={(e) => setPair(e.target.checked)} /> The picture shows BOTH shoes (from the front)
                </label>
              </>
            )}
            {kind === "mustache" && (
              <>
                <select value={beardStyle} onChange={(e) => setBeardStyle(e.target.value)} aria-label="What kind">
                  {BEARD_STYLES.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label}
                    </option>
                  ))}
                </select>
                <Slider label="Up / down" value={fit[0]} min={40} max={66} step={0.2} onChange={(v) => setFit([v, fit[1]])} />
                <Slider label="Size" value={fit[1]} min={5} max={60} step={0.5} onChange={(v) => setFit([fit[0], v])} />
                <label className="sa-check">
                  <input type="checkbox" checked={keepColor} onChange={(e) => setKeepColor(e.target.checked)} /> Keep its own colour (white Santa beard…) — otherwise it matches the hair
                </label>
              </>
            )}
          </div>
        )}

        <div className="avi-actions">
          <button type="button" className="lb-btn" disabled={!pic || busy !== ""} onClick={save}>
            {busy === "save" ? "Saving…" : "💾 Save to the Avatar Maker"}
          </button>
          {pic && (
            <button type="button" className="lb-btn lb-btn-ghost" onClick={reset} disabled={busy === "save"}>
              Start over
            </button>
          )}
        </div>
        {msg && <p className={`avi-msg is-${msg.tone}`}>{msg.text}</p>}
      </div>

      <div className="avi-preview">
        {busy === "cut" && <p className="avi-wait">Cutting out the picture…</p>}
        {!pic && busy !== "cut" && <p className="avi-wait">Pick a picture to see it on the avatar.</p>}
        {pic && (
          <>
            <div className="avi-cut" title="The cut-out picture">
              <img src={pic.url} alt="Cut-out item" />
            </div>
            <div className="avi-avatars">
              {kind !== "shoes" && (
                <span className="avi-av">
                  <AvatarFace look={look} />
                </span>
              )}
              {kind !== "mustache" && (
                <span className="avi-av is-full">
                  <AvatarFace look={look} full />
                </span>
              )}
              {kind === "mustache" && (
                <span className="avi-av">
                  <AvatarFace look={{ ...look, hairColor: "av-hc-blonde", skin: 1 }} />
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* The uploaded list                                                   */
/* ------------------------------------------------------------------ */

const ItemRow = ({ it }) => {
  const [name, setName] = useState(it.name || "");
  const [price, setPrice] = useState(String(it.price ?? 0));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(it.name || "");
    setPrice(String(it.price ?? 0));
  }, [it.name, it.price]);
  const key = KINDS.find((k) => k.id === it.kind)?.key || "top";
  const change = async (patch) => {
    setBusy(true);
    try {
      await updateAvatarItem(it.id, patch);
    } finally {
      setBusy(false);
    }
  };
  const place = it.season ? `season:${it.season}` : `level:${it.level || 1}`;
  return (
    <div className={`avi-item ${it.hidden ? "is-hidden" : ""}`}>
      <span className="avi-thumb">
        <AvatarFace look={{ ...DEFAULT_LOOK, [key]: it.id }} full={it.kind === "shoes"} />
      </span>
      <input className="avi-name" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== it.name && change({ name: name.trim() })} aria-label="Name" />
      <select
        value={place}
        onChange={(e) => {
          const [t, v] = e.target.value.split(":");
          change(t === "season" ? { season: v } : { season: "", level: Number(v) });
        }}
        aria-label="Where it goes"
      >
        {LEVELS.map((l) => (
          <option key={l} value={`level:${l}`}>
            Everyday · Level {l}
          </option>
        ))}
        {SEASONS.map((x) => (
          <option key={x.id} value={`season:${x.id}`}>
            {x.emoji} {x.name}
          </option>
        ))}
      </select>
      <label className="avi-inline">
        <input type="number" min="0" step="10" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={() => Number(price) !== Number(it.price) && change({ price: Math.max(0, Math.floor(Number(price) || 0)) })} aria-label="Price" />
        coins
      </label>
      <label className="sa-check">
        <input type="checkbox" checked={Boolean(it.hidden)} onChange={(e) => change({ hidden: e.target.checked })} /> Hidden
      </label>
      <button
        type="button"
        className="lb-btn lb-btn-ghost lb-btn-sm"
        disabled={busy}
        onClick={() => {
          if (window.confirm(`Delete “${it.name}” for good? Anyone who bought it loses it. (“Hidden” keeps it for them.)`)) deleteAvatarItem(it).catch((e) => window.alert(e?.message || e));
        }}
      >
        🗑 Delete
      </button>
    </div>
  );
};

const AvatarItemsManager = () => {
  const items = useUploadedAvatarItems();
  const [filter, setFilter] = useState("all");
  const shown = items
    .filter((i) => i.id !== PREVIEW_ID && (filter === "all" || i.kind === filter))
    .sort((a, b) => String(a.kind).localeCompare(String(b.kind)) || (a.season || "").localeCompare(b.season || "") || (a.level || 1) - (b.level || 1));
  return (
    <div className="avi-panel">
      <p className="lb-hint">
        Upload your own jackets &amp; shirts, sneakers and mustaches &amp; beards. They appear in every student&apos;s Avatar Maker as soon as you save — everyday (in a Level) or inside a
        holiday season. Price 0 = free.
      </p>
      <AddItem />
      <h3 className="avi-h">Uploaded items ({items.length})</h3>
      <div className="avi-seg is-small">
        {[{ id: "all", label: "All" }, ...KINDS].map((k) => (
          <button key={k.id} type="button" className={filter === k.id ? "is-on" : ""} onClick={() => setFilter(k.id)}>
            {k.label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? <p className="lb-hint">Nothing uploaded here yet.</p> : shown.map((it) => <ItemRow key={it.id} it={it} />)}
    </div>
  );
};

export default AvatarItemsManager;
